import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  Link,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

const useStyles = makeStyles((theme) => ({
  formControl: {
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6400A0",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6400A0",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6400A0",
    },
    "& .MuiOutlinedInput-root.Mui-active .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6400A0",
    },
    "& .MuiTypography-root": {
      fontFamily: "Source Sans Pro !important",
    },
  },
  selectPaperNormal: {
    backgroundColor: "#F4F1F8 !important",
    listStyle: "none",
    margin: 0,
    padding: 0,
    position: "relative",
  },
  selectPaper: {
    backgroundColor: "#F4F1F8 !important",
    listStyle: "none",
    margin: 0,
    padding: 0,
    width: "330px",
    position: "relative",
    overflowX: "auto",
    maxHeight: "257px !important",
  },
  textField: {
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6400A0",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6400A0",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6400A0",
    },
    "& .MuiOutlinedInput-root.Mui-active .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6400A0",
    },
    "& .MuiOutlinedInput-input": {
      paddingBottom: "10.5px",
      paddingTop: "10.5px",
    },
  },
  stickyNav: {
    position: "sticky",
    top: 64,
    zIndex: 1000,
    width: "100%",
    backgroundColor: "#FFF",
    "@media (min-device-width : 320px) and (max-device-width : 767px)": {
      width: "100%",
      backgroundColor: "#FFF",
      position: "relative",
      top: 0,
      zIndex: 0,
    },
    "@media (min-device-width : 768px) and (max-device-width : 1024px)": {
      position: "sticky",
      top: 53,
      zIndex: 1000,
      width: "100%",
      backgroundColor: "#FFF",
    },
  },
  nonStickyNav: {
    width: "100%",
    backgroundColor: "#FFF",
  },
}));

function CaseFilters({
  searchQuery,
  setSearchQuery,
  filteredCases,
  cases,
  onFilter,
  setUserInteracted,
  setCases,
  setFilteredCases,
}) {
  const classes = useStyles();

  const [specialities, setSpecialities] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [bodyParts, setBodyParts] = useState([]);

  const [selectedSpecialities, setSelectedSpecialities] = useState([]);
  const [selectedModalities, setSelectedModalities] = useState([]);
  const [selectedBodyParts, setSelectedBodyParts] = useState([]);
  const [showClearButton, setShowClearButton] = useState(false);
  const [isClearAllClicked, setIsClearAllClicked] = useState(false);

  const isPhone = useMediaQuery("(min-width :320px) and (max-width: 767px)");
  const isTablet = useMediaQuery(
    " (min-device-width : 768px) and (max-device-width : 1024px)"
  );
  const scrollVertical = window.scrollY; // Vertical scroll position

  useEffect(() => {
    const newSpecialities = [];
    const newModalities = [];
    const newBodyParts = [];

    cases.forEach((caseItem) => {
      newSpecialities.push(
        ...caseItem.customFields.find((field) => field.name === "speciality")
          .value
      );
      newModalities.push(
        ...caseItem.customFields.find((field) => field.name === "modality")
          .value
      );
      newBodyParts.push(
        ...caseItem.customFields.find((field) => field.name === "bodypart")
          .value
      );
    });

    setSpecialities([...new Set(newSpecialities)]);
    setModalities([...new Set(newModalities)]);
    setBodyParts([...new Set(newBodyParts)]);

    const params = new URLSearchParams(window.location.search);

    const specialitiesParam = params.get("speciality");
    const modalitiesParam = params.get("modality");
    const bodyPartsParam = params.get("bodypart");

    if (specialitiesParam) {
      setSelectedSpecialities(specialitiesParam.split(","));
    }
    if (modalitiesParam) {
      setSelectedModalities(modalitiesParam.split(","));
    }
    if (bodyPartsParam) {
      setSelectedBodyParts(bodyPartsParam.split(","));
    }
  }, [cases]);

  useEffect(() => {
    if (
      (selectedSpecialities.length > 0 ||
        selectedModalities.length > 0 ||
        selectedBodyParts.length > 0) &&
      !searchQuery
    ) {
      handleApplyFilters(cases);
    } else if (
      (selectedSpecialities.length > 0 ||
        selectedModalities.length > 0 ||
        selectedBodyParts.length > 0) &&
      searchQuery
    ) {
      filteredCases && handleApplyFilters(filteredCases);
    }
  }, [
    selectedSpecialities,
    selectedModalities,
    selectedBodyParts,
    searchQuery,
    filteredCases,
  ]);

  useEffect(() => {
    if (
      selectedSpecialities.length === 0 ||
      selectedModalities.length === 0 ||
      selectedBodyParts.length === 0 ||
      !searchQuery
    ) {
      handleApplyFilters(cases);
    }
  }, [
    selectedSpecialities,
    selectedModalities,
    selectedBodyParts,
    searchQuery,
  ]);

  const handleChangeDropdown = (event, selectId) => {
    const updateImageUrl = (url) => {
      const urlObj = new URL(url);

      return urlObj.toString();
    };

    const updatedCases = cases.map((caseItem) => ({
      ...caseItem,
      imageUrl: updateImageUrl(caseItem.imageUrl),
    }));

    setCases(updatedCases);
    setFilteredCases(updatedCases);
    setIsClearAllClicked(false);

    switch (selectId) {
      case "select1":
        const specialityValue = event.target.value;
        const lastSpecialityValue =
          selectedSpecialities[selectedSpecialities.length - 1];
        const newSpecialityValue = specialityValue[specialityValue.length - 1];

        setSelectedSpecialities(specialityValue);
        updateURL(specialityValue, selectedModalities, selectedBodyParts);
        handleApplyFilters(updatedCases);

        window.digitalData = window.digitalData || {};
        window.digitalData.link = {
          linkName:
            specialityValue.length < selectedSpecialities.length
              ? `unchecked-${lastSpecialityValue}`
              : `checked-${newSpecialityValue}`,
          linkType: "Dicom Viewer Filter",
          linkTitle: "Speciality",
        };

        if (window._satellite) {
          window._satellite.track("link_click");
        }
        window.scrollTo({
          top: isPhone
            ? document.getElementById("imagingapp").offsetTop - 80
            : document.getElementById("imagingapp").offsetTop - 50,
          behavior: "smooth",
        });
        break;
      case "select2":
        const modalityValue = event.target.value;
        const lastModalityValue =
          selectedModalities[selectedModalities.length - 1];
        const newModalityValue = modalityValue[modalityValue.length - 1];

        setSelectedModalities(modalityValue);
        updateURL(selectedSpecialities, modalityValue, selectedBodyParts);
        handleApplyFilters(updatedCases);

        window.digitalData = window.digitalData || {};
        window.digitalData.link = {
          linkName:
            modalityValue.length < selectedModalities.length
              ? `unchecked-${lastModalityValue}`
              : `checked-${newModalityValue}`,
          linkType: "Dicom Viewer Filter",
          linkTitle: "Modality",
        };

        if (window._satellite) {
          window._satellite.track("link_click");
        }
        window.scrollTo({
          top: isPhone
            ? document.getElementById("imagingapp").offsetTop - 80
            : document.getElementById("imagingapp").offsetTop - 50,
          behavior: "smooth",
        });

        break;
      case "select3":
        const bodypartValue = event.target.value;
        const lastBodypartValue =
          selectedBodyParts[selectedBodyParts.length - 1];
        const newBodypartValue = bodypartValue[bodypartValue.length - 1];

        setSelectedBodyParts(bodypartValue);
        updateURL(selectedSpecialities, selectedModalities, bodypartValue);
        handleApplyFilters(updatedCases);

        window.digitalData = window.digitalData || {};
        window.digitalData.link = {
          linkName:
            bodypartValue.length < selectedBodyParts.length
              ? `unchecked-${lastBodypartValue}`
              : `checked-${newBodypartValue}`,
          linkType: "Dicom Viewer Filter",
          linkTitle: "Body Part",
        };

        if (window._satellite) {
          window._satellite.track("link_click");
        }
        window.scrollTo({
          top: isPhone
            ? document.getElementById("imagingapp").offsetTop - 80
            : document.getElementById("imagingapp").offsetTop - 50,
          behavior: "smooth",
        });
        break;
      default:
        break;
    }
  };

  const handleClearFilters = () => {
    const updateImageUrl = (url) => {
      const urlObj = new URL(url);

      return urlObj.toString();
    };

    const updatedCases = cases.map((caseItem) => ({
      ...caseItem,
      imageUrl: updateImageUrl(caseItem.imageUrl),
    }));

    setCases(updatedCases);
    setFilteredCases(updatedCases);

    setSelectedSpecialities([]);
    setSelectedModalities([]);
    setSelectedBodyParts([]);
    setSearchQuery("");
    onFilter(updatedCases);
    updateURL([], [], []);
    setShowClearButton(false);
    setIsClearAllClicked(true);

    window.digitalData = window.digitalData || {};
    window.digitalData.link = {
      linkName: "Clear All",
      linkType: "Dicom Viewer Filter",
      linkTitle: "Clear",
    };

    if (window._satellite) {
      window._satellite.track("link_click");
    }
  };

  const handleSearchQuery = (e) => {
    setIsClearAllClicked(false);
    setSearchQuery(e.target.value);
    setShowClearButton(true);
    !e.target.value && setShowClearButton(false);
    window.scrollTo({
      top: isPhone
        ? document.getElementById("imagingapp").offsetTop - 80
        : document.getElementById("imagingapp").offsetTop - 50,
      behavior: "smooth",
    });
  };

  const handleClearSearchQuery = (e) => {
    setIsClearAllClicked(false);
    e.preventDefault();
    setSearchQuery("");
    setShowClearButton(false);

    window.scrollTo({
      top: isPhone
        ? document.getElementById("imagingapp").offsetTop - 80
        : document.getElementById("imagingapp").offsetTop - 50,
      behavior: "smooth",
    });
  };

  const handleApplyFilters = (cases) => {
    const filteredCases = cases.filter((caseItem) => {
      const caseSpeciality = caseItem.customFields.find(
        (field) => field.name === "speciality"
      ).value;
      const caseModality = caseItem.customFields.find(
        (field) => field.name === "modality"
      ).value;
      const caseBodyPart = caseItem.customFields.find(
        (field) => field.name === "bodypart"
      ).value;

      const specialityMatch = selectedSpecialities.length
        ? selectedSpecialities.some((speciality) =>
            caseSpeciality.includes(speciality)
          )
        : true;

      const modalityMatch = selectedModalities.length
        ? selectedModalities.some((modality) => caseModality.includes(modality))
        : true;

      const bodyPartMatch = selectedBodyParts.length
        ? selectedBodyParts.some((bodyPart) => caseBodyPart.includes(bodyPart))
        : true;

      return specialityMatch && modalityMatch && bodyPartMatch;
    });

    onFilter(filteredCases.length ? filteredCases : []);
    setIsClearAllClicked(false);
  };

  const updateURL = (specialities, modalities, bodyParts) => {
    const params = new URLSearchParams(window.location.search);

    params.set("speciality", specialities.join(","));
    params.set("modality", modalities.join(","));
    params.set("bodypart", bodyParts.join(","));

    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newURL);
  };

  specialities.sort();
  modalities.sort();
  bodyParts.sort();

  return (
    <>
      <div
        id="stickyNav"
        className={isClearAllClicked ? classes.nonStickyNav : classes.stickyNav}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              className={classes.formControl}
            >
              <Select
                multiple
                value={selectedSpecialities}
                onChange={(e) => handleChangeDropdown(e, "select1")}
                displayEmpty
                renderValue={(selected) =>
                  selected.length > 0
                    ? `Speciality (${selected.length})`
                    : "Speciality"
                }
                MenuProps={{
                  classes: {
                    paper:
                      specialities.length > 6
                        ? classes.selectPaper
                        : classes.selectPaperNormal,
                  },
                }}
                sx={{
                  display: "flex",
                  height: "44px",
                  width: "100%",
                  padding: "8px 16px",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "12px",

                  fontFamily: "Source Sans Pro",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "24px",
                }}
              >
                {specialities.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "#F4F1F8",
                        fontWeight: "700",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "#F4F1F8",
                      },
                      "&.MuiMenuItem-root:hover": {
                        backgroundColor: "#F4F1F8",
                      },
                      "&.MuiMenuItem-root:focus-visible": {
                        backgroundColor: "#F4F1F8",
                      },
                      transition: "none",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: "400",
                      lineHeight: "24px",
                      color: "#222",
                      display: specialities.length > 6 && "inline-flex",
                      width: specialities.length > 6 && "50%",
                    }}
                    disableRipple
                  >
                    <Checkbox
                      checked={selectedSpecialities.indexOf(option) > -1}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiMenuItem-root:hover": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiMenuItem-root:focus-visible": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiCheckbox-root.Mui-checked": {
                          color: "#6022A6 !important",
                        },
                        "&.MuiSvgIcon-root": {
                          borderRadius: "8px",
                        },
                        transition: "none",
                        padding: "2px",
                      }}
                      disableRipple
                    />
                    <ListItemText
                      primary={option}
                      sx={{
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "24px",
                        color: "#222",
                      }}
                      primaryTypographyProps={{
                        fontFamily: "Source Sans Pro",
                        fontWeight:
                          selectedSpecialities.indexOf(option) > -1 && "700",
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              className={classes.formControl}
            >
              <Select
                multiple
                value={selectedModalities}
                onChange={(e) => handleChangeDropdown(e, "select2")}
                displayEmpty
                renderValue={(selected) =>
                  selected.length > 0
                    ? `Modality/Technology (${selected.length})`
                    : "Modality/Technology"
                }
                MenuProps={{
                  classes: {
                    paper:
                      modalities.length > 6
                        ? classes.selectPaper
                        : classes.selectPaperNormal,
                  },
                }}
                sx={{
                  display: "flex",
                  height: "44px",
                  width: "100%",
                  padding: "8px 16px",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "12px",

                  fontFamily: "Source Sans Pro",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "24px",
                }}
              >
                {modalities.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "#F4F1F8",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "#F4F1F8",
                      },
                      "&.MuiMenuItem-root:hover": {
                        backgroundColor: "#F4F1F8",
                      },
                      "&.MuiMenuItem-root:focus-visible": {
                        backgroundColor: "#F4F1F8",
                      },
                      transition: "none",
                      fontFamily: "Source Sans Pro",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: "400",
                      lineHeight: "24px",
                      display: modalities.length > 6 && "inline-flex",
                      width: modalities.length > 6 && "50%",
                    }}
                    disableRipple
                  >
                    <Checkbox
                      checked={selectedModalities.indexOf(option) > -1}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiMenuItem-root:hover": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiMenuItem-root:focus-visible": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiCheckbox-root.Mui-checked": {
                          color: "#6022A6 !important",
                        },
                        transition: "none",
                        padding: "2px",
                      }}
                      disableRipple
                    />
                    <ListItemText
                      primary={option}
                      primaryTypographyProps={{
                        fontFamily: "Source Sans Pro",
                        fontWeight:
                          selectedModalities.indexOf(option) > -1 && "700",
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl
              fullWidth
              variant="outlined"
              size="small"
              className={classes.formControl}
            >
              <Select
                multiple
                value={selectedBodyParts}
                onChange={(e) => handleChangeDropdown(e, "select3")}
                displayEmpty
                renderValue={(selected) =>
                  selected.length > 0
                    ? `Body Part (${selected.length})`
                    : "Body Part"
                }
                MenuProps={{
                  classes: {
                    paper:
                      bodyParts.length > 6
                        ? classes.selectPaper
                        : classes.selectPaperNormal,
                  },
                }}
                sx={{
                  display: "flex",
                  height: "44px",
                  width: "100%",
                  padding: "8px 16px",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "12px",

                  fontFamily: "Source Sans Pro",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "24px",
                }}
              >
                {bodyParts.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      "&.Mui-selected": {
                        backgroundColor: "#F4F1F8",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "#F4F1F8",
                      },
                      "&.MuiMenuItem-root:hover": {
                        backgroundColor: "#F4F1F8",
                      },
                      "&.MuiMenuItem-root:focus-visible": {
                        backgroundColor: "#F4F1F8",
                      },
                      transition: "none",
                      display: bodyParts.length > 6 && "inline-flex",
                      width: bodyParts.length > 6 && "50%",
                    }}
                    disableRipple
                  >
                    <Checkbox
                      checked={selectedBodyParts.indexOf(option) > -1}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiMenuItem-root:hover": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiMenuItem-root:focus-visible": {
                          backgroundColor: "#F4F1F8",
                        },
                        "&.MuiCheckbox-root.Mui-checked": {
                          color: "#6022A6 !important",
                        },
                        transition: "none",
                        fontFamily: "Source Sans Pro",
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "24px",
                        padding: "2px",
                      }}
                      disableRipple
                    />
                    <ListItemText
                      primary={option}
                      primaryTypographyProps={{
                        fontFamily: "Source Sans Pro",
                        fontWeight:
                          selectedBodyParts.indexOf(option) > -1 && "700",
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              id="searchBox"
              fullWidth
              value={searchQuery}
              onInput={handleSearchQuery}
              onFocus={() => setUserInteracted(true)}
              variant="outlined"
              placeholder="Search"
              size="small"
              style={{
                marginRight: "16px",
                display: "flex",
                alignItems: "center",
                height: "44px",
                width: "100%",
                gap: "8px",
                flex: "1 0 0",
                alignSelf: "stretch",
                opacity: "0.8",
                borderRadius: "12px",
                textTransform: "none",
              }}
              InputProps={{
                style: { borderRadius: "12px" },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color: "#6400A0",
                        display: "flex",
                        //minWidth: "fit-content",
                        height: "24px",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {showClearButton && (
                      <Button
                        variant="text"
                        onClick={handleClearSearchQuery}
                        sx={{
                          padding: 0,
                          "&.MuiButton-root:hover": {
                            backgroundColor: "#FFF",
                          },
                          //minWidth: "fit-content",
                        }}
                        disableRipple
                      >
                        <CloseIcon sx={{ color: "#6022A6" }} />
                      </Button>
                    )}
                  </InputAdornment>
                ),
                sx: {
                  fontFamily: "Source Sans Pro",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "400",
                  lineHeight: "24px",
                },
              }}
              inputProps={{
                sx: {
                  "&::placeholder": {
                    fontFamily: "Source Sans Pro",
                    fontSize: "16px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "24px",
                    opacity: "0.8",
                  },
                },
              }}
              className={classes.textField}
            />
          </Grid>
        </Grid>

        {(selectedSpecialities.length > 0 ||
          selectedModalities.length > 0 ||
          selectedBodyParts.length > 0) && (
          <Grid container spacing={1} mt={1}>
            <Grid item xs={12}>
              <hr style={{ width: "100%", border: "0.952px solid #D9D9D9" }} />
            </Grid>
          </Grid>
        )}
        <div>
          <Grid container spacing={1} mt={1}>
            <Grid item xs={12} paddingBottom="16px">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  alignSelf: "stretch",
                  flexWrap: "wrap",
                }}
              >
                {selectedSpecialities.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    sx={{
                      display: "flex",
                      padding: "10px 12px 10px 24px",
                      alignItems: "center",
                      gap: "8px",
                      borderRadius: "22px",
                      border: "1px solid #C7C8CF",
                      background: "#FBF9FD",
                      fontFamily: "Source Sans Pro",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: "400",
                      lineHeight: "24px",
                      "&:hover": {
                        borderColor: "#6400A0",
                      },
                    }}
                    deleteIcon={
                      <CloseIcon
                        sx={{
                          display: "flex",
                          width: "16px",
                          height: "16px",
                          alignItems: "flex-start",
                          flexShrink: 0,
                        }}
                      />
                    }
                    onDelete={() => {
                      const updatedSpecialities = selectedSpecialities.filter(
                        (item) => item !== value
                      );
                      setSelectedSpecialities(updatedSpecialities);
                      updateURL(
                        updatedSpecialities,
                        selectedModalities,
                        selectedBodyParts
                      );
                      window.scrollTo({
                        top: isPhone
                          ? document.getElementById("imagingapp").offsetTop - 80
                          : document.getElementById("imagingapp").offsetTop -
                            50,
                        behavior: "smooth",
                      });
                      window.digitalData = window.digitalData || {};
                      window.digitalData.link = {
                        linkName: `Remove-${value}`,
                        linkType: "Dicom Viewer Filter",
                        linkTitle: "Speciality",
                      };

                      if (window._satellite) {
                        window._satellite.track("link_click");
                      }
                    }}
                  />
                ))}
                {selectedModalities.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    sx={{
                      display: "flex",
                      padding: "10px 12px 10px 24px",
                      alignItems: "center",
                      gap: "8px",
                      borderRadius: "22px",
                      border: "1px solid #C7C8CF",
                      background: "#FBF9FD",
                      fontFamily: "Source Sans Pro",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: "400",
                      lineHeight: "24px",
                      "&:hover": {
                        borderColor: "#6400A0",
                      },
                    }}
                    deleteIcon={
                      <CloseIcon
                        sx={{
                          display: "flex",
                          width: "16px",
                          height: "16px",
                          alignItems: "flex-start",
                          flexShrink: 0,
                        }}
                      />
                    }
                    onDelete={() => {
                      const updatedModalities = selectedModalities.filter(
                        (item) => item !== value
                      );
                      setSelectedModalities(updatedModalities);
                      updateURL(
                        selectedSpecialities,
                        updatedModalities,
                        selectedBodyParts
                      );
                      window.scrollTo({
                        top: isPhone
                          ? document.getElementById("imagingapp").offsetTop - 80
                          : document.getElementById("imagingapp").offsetTop -
                            50,
                        behavior: "smooth",
                      });
                      window.digitalData = window.digitalData || {};
                      window.digitalData.link = {
                        linkName: `Remove-${value}`,
                        linkType: "Dicom Viewer Filter",
                        linkTitle: "Modality",
                      };

                      if (window._satellite) {
                        window._satellite.track("link_click");
                      }
                    }}
                  />
                ))}
                {selectedBodyParts.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    sx={{
                      display: "flex",
                      padding: "10px 12px 10px 24px",
                      alignItems: "center",
                      gap: "8px",
                      borderRadius: "22px",
                      border: "1px solid #C7C8CF",
                      background: "#FBF9FD",
                      fontFamily: "Source Sans Pro",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: "400",
                      lineHeight: "24px",
                      "&:hover": {
                        borderColor: "#6400A0",
                      },
                    }}
                    deleteIcon={
                      <CloseIcon
                        sx={{
                          display: "flex",
                          width: "16px",
                          height: "16px",
                          alignItems: "flex-start",
                          flexShrink: 0,
                        }}
                      />
                    }
                    onDelete={() => {
                      const updatedBodyParts = selectedBodyParts.filter(
                        (item) => item !== value
                      );
                      setSelectedBodyParts(updatedBodyParts);
                      updateURL(
                        selectedSpecialities,
                        selectedModalities,
                        updatedBodyParts
                      );
                      window.scrollTo({
                        top: isPhone
                          ? document.getElementById("imagingapp").offsetTop - 80
                          : document.getElementById("imagingapp").offsetTop -
                            50,
                        behavior: "smooth",
                      });
                      window.digitalData = window.digitalData || {};
                      window.digitalData.link = {
                        linkName: `Remove-${value}`,
                        linkType: "Dicom Viewer Filter",
                        linkTitle: "Body Part",
                      };

                      if (window._satellite) {
                        window._satellite.track("link_click");
                      }
                    }}
                  />
                ))}
                {(selectedSpecialities.length > 0 ||
                  selectedModalities.length > 0 ||
                  selectedBodyParts.length > 0) && (
                  <>
                    {/* <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    color: "#6022A6",
                  }}
                  onClick={handleApplyFilters}
                >
                  Apply
                </Button> */}
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => {
                        handleClearFilters();
                        // scrollVertical !==
                        //   document.getElementById("imagingapp").offsetTop &&
                        window.scrollTo({
                          top: isPhone
                            ? document.getElementById("imagingapp").offsetTop -
                              80
                            : document.getElementById("imagingapp").offsetTop -
                              50,
                          behavior: "smooth",
                        });
                      }}
                      sx={{
                        color: "#6022A6",
                        fontFamily: "Source Sans Pro",
                        fontSize: "18px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "28px",
                        textDecoration: "none",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Clear All
                    </Link>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </div>
      </div>
    </>
  );
}

export default CaseFilters;
